import { useCallback, useRef } from 'react';

import { TileCarousel } from 'components/TileCarousel';
import { useElementReachedEffect } from 'hooks/viewport/useElementReachedEffect';
import { SalariesSubmittedData } from 'modules/salaries/types/SalariesSubmittedData';
import { useSearchAlgoliaSalaryRecommendedJobs } from 'modules/search/hooks/useSearchAlgoliaSalaryRecommendedJobs';
import { SearchPageLocationState } from 'modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetJobsRoutePath } from 'modules/search/routing/helpers/searchGetJobsRoutePath';
import { SearchLocation } from 'modules/search/types/SearchLocation';
import { useMainSelector } from 'store/hooks/useMainSelector';
import { trackEventOnce } from 'utils/analytics/track/trackEventOnce';

import { RelatedJobsContainer, RelatedJobsWrapper } from './RelatedJobs.styled';

type StandardCmsRelatedJobsProps = {
  headline: string;
  submittedData?: SalariesSubmittedData;
};

export function RelatedJobs({
  submittedData = {},
  headline,
}: StandardCmsRelatedJobsProps) {
  const {
    selectedSynonymFamilyId,
    selectedSynonymId,
    jobTitleSelected,
    city,
    geoType,
    latitude,
    longitude,
    stateCode,
  } = submittedData;
  const workLocation =
    city && stateCode
      ? {
          geoType,
          latitude: latitude ? parseFloat(latitude.toFixed(4)) : undefined,
          longitude: longitude ? parseFloat(longitude.toFixed(4)) : undefined,
          text: `${city}, ${stateCode}`,
        }
      : undefined;
  const relatedJobsRef = useRef<HTMLDivElement>(null);

  const searchLocation = useMainSelector(
    (state) => state.mainSearch.searchLocation,
  ) as SearchLocation;

  const { filtersUsed, resultsType, recommendedJobs, queryId } =
    useSearchAlgoliaSalaryRecommendedJobs({
      searchLocation: workLocation || searchLocation,
      options: {
        familyId: selectedSynonymFamilyId,
        jobTitle: jobTitleSelected,
      },
    });

  useElementReachedEffect({
    ref: relatedJobsRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      trackEventOnce('Displayed: Featured Listings', {
        context: 'Search Promo',
        carousel_location: 'Salary Report',
        listing_type: 'JOB',
        listing_count: recommendedJobs.length,
        layout: 'REGULAR',
        results_type: resultsType,
        query_id: queryId,
      });
    }, [recommendedJobs, resultsType, queryId]),
  });

  if (
    !recommendedJobs ||
    (Array.isArray(recommendedJobs) && recommendedJobs.length === 0)
  )
    return null;

  type AdditionalAnalytics = {
    job_family_id?: string;
  };

  const additionalAnalyticsProps = {} as AdditionalAnalytics;

  if (selectedSynonymFamilyId)
    additionalAnalyticsProps.job_family_id = selectedSynonymFamilyId;

  const seeAllUrl = searchGetJobsRoutePath({
    lang: CURRENT_LOCALE,
    q: filtersUsed.jobTitle ? filtersUsed.jobTitle : '',
    filtersByName: {
      synonymId: filtersUsed.familyId ? selectedSynonymId : undefined,
    },
  });

  return (
    <RelatedJobsContainer ref={relatedJobsRef}>
      <RelatedJobsWrapper data-qa-id="recommended-jobs-section">
        <TileCarousel
          headline={headline}
          results={recommendedJobs}
          seeAll={{
            /* TRANSLATORS: Link to job search */
            text: getText('See all jobs'),
            action: {
              type: 'link',
              url: seeAllUrl,
              state: {
                searchLocation: filtersUsed.searchLocation,
              } satisfies SearchPageLocationState,
            },
          }}
          listingType="JOB"
          carouselLocation="Salary Report"
          additionalAnalyticsProps={additionalAnalyticsProps}
        />
      </RelatedJobsWrapper>
    </RelatedJobsContainer>
  );
}
